import { Alert, Button, Select } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';

import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { useClinicalInterpretationModalLogic } from '../clinical_observation/hooks';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';

export type ClinicalInterpretationModalProps = {
  onClose: () => void;
  evaluationEntityId: number;
  variantId: number;
  defaultValue?: string;
};

const ClinicalInterpretationModal: React.FC<
  ClinicalInterpretationModalProps
> = ({ onClose, evaluationEntityId, variantId, defaultValue }) => {
  const { isReadOnly, canInteract } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isSubmitting,
    value,
    setValue,
    commentEditorState,
    options,
    modalOpts,
    isSaveDisabled,
    handleSubmit,
    handleEditorChange,
  } = useClinicalInterpretationModalLogic({
    onClose,
    evaluationEntityId,
    variantId,
    defaultValue,
  });

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="c-form-field">
        <div className="c-form-field__label">Clinical interpretation:</div>
        <div className="c-form-field__wrapper">
          <Select
            value={value}
            onChange={(v) => setValue(v)}
            className="h-proband-modal__select-field"
            options={options}
            placeholder="Choose clinical interpretation"
            disabled={!canInteract}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </div>
      </div>

      <div className="c-form-field">
        <div className="c-form-field__label">Comment:</div>
        <div className="c-form-field__wrapper">
          <RichTextEditor
            name="comment"
            editorState={commentEditorState}
            onChange={handleEditorChange}
            placeholder=""
            label=""
            readOnly={!canInteract}
          />
        </div>
      </div>

      <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
        <Button
          type="primary"
          onClick={handleSubmit}
          className="h-proband-modal__form-submit-btn"
          loading={isSubmitting}
          disabled={isSaveDisabled}
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
};

export default ClinicalInterpretationModal;
